export interface VmPatient {
  id: string;
  name: string;
  phone: string;
  dob: string;
}

/**
 * Fallback notice, shown only until the server's own wording arrives.
 *
 * Not the source of truth: {@link VmSettings.disclosure} is, because it is the text the telephony
 * flow actually speaks and the version stamped on every voicemail. Anything written here is a second
 * copy of a legal notice, and the two drifted apart once already.
 */
export const VM_DISCLOSURE =
  'Your voicemail will be recorded and automatically transcribed for your healthcare provider. Please do not use this voicemail for urgent or emergency medical needs. If you are experiencing a medical emergency, please hang up and dial 911. For urgent medical concerns, please contact the appropriate urgent care or emergency service.';

/**
 * Fallback text-link prompt, shown only until the server's own wording arrives.
 *
 * Not the source of truth: {@link VmSettings.smsOffer} is, for the same reason as the notice. The
 * copy that used to live here had drifted from what calls say, so the preview spoke one thing while
 * the dialog showed another.
 */
export const VM_SMS_OFFER =
  'To receive a text message with a link to schedule an appointment, press 1. You can then leave a voicemail.';

/** Fallback for what a caller hears after pressing 1, before the tone. See {@link VmSettings.smsConfirmation}. */
export const VM_SMS_CONFIRMATION =
  'Thank you. We will text you that link shortly.';

/** Which caller the call preview plays for. */
export type VmPreviewCall = 'standard' | 'booked';

/** The settings on screen, saved or not, to be spoken the way a call speaks them. */
export interface VmCallPreviewRequest {
  greeting: string;
  call: VmPreviewCall;
  /** The override link when "Send a different link by text" is on; otherwise null. */
  bookingLinkOverride: string | null;
}

export type VmSmsStatus =
  | 'sent'
  | 'sending'
  | 'not-sent'
  | 'rejected'
  | 'unknown';

/**
 * Why no booking text went out. The first five mean the caller never heard the prompt; the rest
 * mean the prompt played but nothing was sent.
 */
export type VmSmsReason =
  | 'appointment-booked'
  | 'eligibility-unavailable'
  | 'no-number'
  | 'sms-unavailable'
  | 'no-destination'
  | 'availability-unavailable'
  | 'no-response'
  | 'not-requested'
  | 'input-unavailable'
  | 'opted-out'
  | 'delivery-blocked'
  | 'match-changed'
  | 'send-expired'
  | 'recently-sent'
  | 'line-limit-reached';

export interface VmSmsResult {
  status: VmSmsStatus;
  /** Set while status is 'not-sent'; null once a send was attempted. */
  reason: VmSmsReason | null;
  attemptedAt: string | null;
  /** Time the service accepted the message, not a delivery receipt. */
  sentAt: string | null;
  destination: string | null;
  /** Body captured when the send attempt was created. */
  message: string | null;
}

export type VmTranscriptStatus = 'ready' | 'processing' | 'failed';

/** One voicemail's transcript. `text` is empty unless the status is ready. */
export interface VmTranscript {
  status: VmTranscriptStatus;
  text: string;
}

export interface Voicemail {
  id: string;
  callerNumber: string | null;
  patient: VmPatient | null;
  receivedAt: string;
  duration: number;
  /**
   * Opaque handle the gateway resolves to audio, empty when no recording was captured.
   * Not a URL the browser can fetch on its own: the recording is PHI, so playback goes through an
   * authenticated request rather than a link that would work for anyone holding it.
   */
  recordingRef: string;
  unread: boolean;
  /**
   * Status only. The text is a patient talking about their health, so it is fetched per voicemail
   * through {@link VoicemailGateway.getTranscript} and that read is audited — returning it with the
   * inbox handed over every transcript a provider owned in one call that nothing recorded.
   */
  transcript: { status: VmTranscriptStatus };
  /** Captured at the original call; manual patient linking never changes it. */
  sms: VmSmsResult;
  matchType: 'automatic' | 'manual' | 'unknown';
}

/**
 * How the API resolved the booking link. It reports which rule won, not which Scheduling type the
 * provider chose: `scheduling-link` is a Link type, `landing-page` covers Scheduler and Embedded
 * alike, and `override` means the provider's own link replaced both.
 */
export type VmSchedulingSource =
  | 'override'
  | 'scheduling-link'
  | 'landing-page';

/**
 * Where booking texts send callers, resolved by the API rather than entered again.
 * `url` is null when nothing resolves: no Scheduling type chosen, landing page unpublished, or an
 * unusable Link URL. The API does not distinguish those cases, so the screen explains them together.
 */
export interface VmBookingLink {
  url: string | null;
  source: VmSchedulingSource | null;
}

export interface VmSettings {
  state:
    | 'not-configured'
    | 'provisioning'
    | 'active'
    | 'failed'
    | 'checking'
    | 'support-required';
  phoneNumber: string | null;
  practicePhone: string;
  providerName: string;
  greeting: string;
  /**
   * "Send a different link by text": an absolute HTTPS URL that replaces the scheduling destination
   * for these texts only. Empty when the switch is off. This is the only booking setting stored
   * with the voicemail line.
   */
  overrideUrl: string;
  bookingLink: VmBookingLink;
  /** Separate from voice activation. No provider control can enable this program. */
  smsAvailable: boolean;
  /**
   * The required notice exactly as callers hear it, served rather than kept here.
   *
   * A copy in the client is a copy that drifts: the one that used to live here promised "After the
   * tone, please leave your message. Thank you, and we look forward to assisting you.", which no
   * caller was ever played. A provider reading the notice has to be reading the notice.
   */
  disclosure: string;
  disclosureVersion: string;
  /** The text-link prompt exactly as callers hear it, served for the same reason as the notice. */
  smsOffer: string;
  /** What a caller hears after pressing 1, before the tone. */
  smsConfirmation: string;
  /**
   * The booking text exactly as it is sent, with "[booking link]" in place of the link. Served for
   * the same reason as the notice: the copy that used to live here had drifted from what is sent.
   */
  smsMessage: string;
  /** Greeting limit the server enforces. Duplicating it here is how the form and the API disagree. */
  maxGreetingLength: number;
  /**
   * Whether the provider's website shows the voicemail number rather than the practice phone while
   * the line is active. True unless the provider switched it off. Saved on its own, as the switch
   * is flipped, rather than with the greeting.
   */
  useVoicemailNumberOnWebsite: boolean;
}

export type VmDemoScenario =
  | 'default'
  | 'populated'
  | 'setup'
  | 'not-configured'
  | 'empty'
  | 'processing'
  | 'load-error'
  | 'provisioning-failed'
  | 'provisioning'
  | 'checking'
  | 'support-required'
  | 'no-destination'
  | 'unpublished-landing-page'
  | 'override-link'
  | 'appointment-booked'
  | 'eligibility-unavailable'
  | 'unknown-outcome'
  | 'withheld'
  | 'audio-error'
  | 'sms-sending'
  | 'search-error'
  | 'link-error'
  | 'save-error'
  | 'sms-unavailable'
  | 'sms-unavailable-setup'
  | 'no-response'
  | 'not-requested'
  | 'input-unavailable'
  | 'opted-out'
  | 'delivery-blocked'
  | 'match-changed';
